/**
 * Application Constants and Configuration
 * Centralized configuration for all environment variables and constants
 */

import { environmentConfig } from './environment';

// AWS Cognito Configuration (from environment)
export const COGNITO_CONFIG = {
  REGION: environmentConfig.cognito.region,
  USER_POOL_ID: environmentConfig.cognito.userPoolId,
  CLIENT_ID: environmentConfig.cognito.clientId,
  IDP_ENDPOINT: environmentConfig.cognito.idpEndpoint,
  AUTH_FLOW: environmentConfig.cognito.authFlow,
} as const;

// Socket.io Configuration (from environment)
export const SOCKET_CONFIG = {
  URL: environmentConfig.socket.url,
  RECONNECTION_DELAY: environmentConfig.socket.reconnectionDelay,
  RECONNECTION_DELAY_MAX: environmentConfig.socket.reconnectionDelayMax,
  RECONNECTION_ATTEMPTS: environmentConfig.socket.reconnectionAttempts,
  RANDOMIZATION_FACTOR: environmentConfig.socket.randomizationFactor,
} as const;

// Session Configuration (from environment)
export const SESSION_CONFIG = {
  IDLE_TIMEOUT: environmentConfig.session.idleTimeout,
  WARNING_DURATION: environmentConfig.session.warningDuration,
  TOKEN_STORAGE_KEY: 'auth_tokens',
} as const;

// API Configuration (from environment)
export const API_CONFIG = {
  REQUEST_TIMEOUT: environmentConfig.api.requestTimeout,
  MAX_RETRIES: environmentConfig.api.maxRetries,
  CONFIG_ENDPOINT: environmentConfig.api.configEndpoint,
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  INVALID_CREDENTIALS: 'Invalid username or password.',
  // AL-472: USER_NOT_CONFIRMED and USER_NOT_FOUND were removed, not renamed. Both told an
  // anonymous caller whether an account exists. Neither had a single reference, so nothing
  // needed rewiring — but they were sitting here ready to be picked up. Use
  // INVALID_CREDENTIALS for every credential failure regardless of cause.
  PASSWORD_MISMATCH: 'Passwords do not match.',
  PASSWORD_TOO_SHORT: 'Password must be at least 8 characters.',
  CHALLENGE_UNSUPPORTED: (challenge: string) => `Unsupported challenge: ${challenge}`,
  TIMEOUT: 'Request timed out. Please try again.',
  PASSWORD_REQUIREMENTS:
    'Password must be 8+ characters with uppercase, lowercase, numbers, and symbols.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Successfully logged in.',
  LOGOUT_SUCCESS: 'Successfully logged out.',
  PASSWORD_RESET_INITIATED: 'Password reset code sent to your email.',
  PASSWORD_RESET_CONFIRMED: 'Password reset successfully.',
  NEW_PASSWORD_SET: 'New password set successfully.',
} as const;

// Validation Rules
export const VALIDATION_RULES = {
  PASSWORD_MIN_LENGTH: 8,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  USERNAME_MIN_LENGTH: 1,
} as const;

// Log Levels for debugging
export enum LOG_LEVEL {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

export const CURRENT_LOG_LEVEL = LOG_LEVEL.ERROR;
